/**
 * React hook for managing PWA installation prompts
 */

import { useState, useEffect, useCallback, useRef } from "react";

/** Detected platform for installation instructions */
export type InstallPlatform =
  | "chrome-desktop"
  | "safari-desktop"
  | "safari-ios"
  | "android-chrome"
  | "other";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface UsePwaInstallResult {
  /** Detected platform */
  platform: InstallPlatform;
  /** Whether the app can be installed (native prompt available or manual instructions applicable) */
  canInstall: boolean;
  /** Whether the app is already running in standalone mode */
  isInstalled: boolean;
  /** Whether native prompt is available (Chrome/Edge/Android) */
  hasNativePrompt: boolean;
  /** Whether the install prompt has been dismissed by user */
  isDismissed: boolean;
  /** Trigger native install prompt (Chrome/Edge/Android only) */
  promptInstall: () => Promise<boolean>;
  /** Dismiss the install prompt (persists for 7 days) */
  dismissPrompt: () => void;
  /** Clear dismissal state */
  clearDismissal: () => void;
}

const DISMISS_KEY = "pwa-install-dismissed";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/** Detect platform for install instructions */
function detectPlatform(): InstallPlatform {
  if (typeof navigator === "undefined") return "other";

  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isMacOS = /Macintosh/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua);
  const isChrome = /Chrome/.test(ua) && !/Edge|Edg/.test(ua);
  const isAndroid = /Android/.test(ua);

  if (isIOS && isSafari) return "safari-ios";
  if (isMacOS && isSafari) return "safari-desktop";
  if (isAndroid && (isChrome || /CriOS/.test(ua))) return "android-chrome";
  if (isChrome) return "chrome-desktop";
  return "other";
}

/** Check if running in standalone mode (already installed) */
function isStandalone(): boolean {
  if (typeof window === "undefined") return false;

  // iOS Safari standalone mode
  if ((navigator as unknown as { standalone?: boolean }).standalone === true) {
    return true;
  }

  // Standard PWA display mode
  if (window.matchMedia("(display-mode: standalone)").matches) {
    return true;
  }

  // Also check for fullscreen and minimal-ui modes
  if (window.matchMedia("(display-mode: fullscreen)").matches) {
    return true;
  }
  if (window.matchMedia("(display-mode: minimal-ui)").matches) {
    return true;
  }

  return false;
}

/** Check if dismissal is still valid */
function isDismissalValid(): boolean {
  if (typeof localStorage === "undefined") return false;

  const dismissed = localStorage.getItem(DISMISS_KEY);
  if (!dismissed) return false;

  const dismissedAt = parseInt(dismissed, 10);
  if (isNaN(dismissedAt)) return false;

  return Date.now() - dismissedAt < DISMISS_DURATION_MS;
}

export function usePwaInstall(): UsePwaInstallResult {
  const [platform] = useState<InstallPlatform>(() => detectPlatform());
  const [isInstalled, setIsInstalled] = useState(() => isStandalone());
  const [isDismissed, setIsDismissed] = useState(() => isDismissalValid());
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [hasNativePrompt, setHasNativePrompt] = useState(false);

  // Listen for beforeinstallprompt event (Chrome/Edge/Android)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome from showing the default prompt
      e.preventDefault();
      // Stash the event for later use
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setHasNativePrompt(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      deferredPromptRef.current = null;
      setHasNativePrompt(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // Listen for display mode changes (user installed via browser menu)
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const handleDisplayModeChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        setIsInstalled(true);
      }
    };
    mediaQuery.addEventListener("change", handleDisplayModeChange);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
      mediaQuery.removeEventListener("change", handleDisplayModeChange);
    };
  }, []);

  // Determine if installation is possible
  const canInstall =
    !isInstalled &&
    (hasNativePrompt ||
      platform === "safari-ios" ||
      platform === "safari-desktop" ||
      platform === "android-chrome");

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPromptRef.current) {
      return false;
    }

    try {
      await deferredPromptRef.current.prompt();
      const { outcome } = await deferredPromptRef.current.userChoice;

      if (outcome === "accepted") {
        setIsInstalled(true);
        deferredPromptRef.current = null;
        setHasNativePrompt(false);
        return true;
      }

      return false;
    } catch (err) {
      console.error("[PWA] Install prompt error:", err);
      return false;
    }
  }, []);

  const dismissPrompt = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setIsDismissed(true);
  }, []);

  const clearDismissal = useCallback(() => {
    localStorage.removeItem(DISMISS_KEY);
    setIsDismissed(false);
  }, []);

  return {
    platform,
    canInstall,
    isInstalled,
    hasNativePrompt,
    isDismissed,
    promptInstall,
    dismissPrompt,
    clearDismissal,
  };
}
